/**
 * The acquisition vocabulary: one function, one enum, no boolean and no
 * `=== 0` anywhere else in this area.
 */

import type { Credits, FundedWindow } from '../api/schemas';

/** FOUR values, not a boolean and not a number.
 *
 *  `unknown` is a first-class state because the query can fail and a failed
 *  balance read must not render as "waiting on credits" -- that would tell a
 *  paying tenant their work stopped.
 *
 *  `partially-funded` is a first-class state because §18.5 sheds accounts
 *  until the remainder buys a usable window. Collapsing it into `running`
 *  tells a tenant whose nine lowest-priority accounts were shed that
 *  everything is fine; collapsing it into `waiting-on-credits` tells a tenant
 *  with live collection that nothing is happening. Both are wrong in the
 *  expensive direction, and there is no third place to put the case. */
export type AcquisitionState = 'waiting-on-credits' | 'partially-funded' | 'running' | 'unknown';

/** §18.5: a hold reduces available without reducing the balance, and
 *  available may be negative. The single definition of "can anything run".
 *
 *  `credits` decides waiting vs. not: a non-positive balance is exhausted
 *  regardless of what is held, and a negative available balance (a hold that
 *  overran it) is exhausted too, even on a technically positive balance --
 *  but credit held against a POSITIVE balance and a non-negative available
 *  figure is money reserved for work in flight, not exhaustion, and reads as
 *  `running`.
 *
 *  The window is the SERVER's answer, never a division done here. This
 *  function reads whether a shed set exists; it does not compute one, and it
 *  does not divide `available` by anything -- `window.credits_per_hour` is
 *  never read below. When the window is absent -- because the route has not
 *  landed, or its own read failed -- a positive balance is `running`, which
 *  is the honest reading of "we know there is credit and we do not know what
 *  it bought". */
export function acquisitionState(
  credits: Credits | undefined,
  window?: FundedWindow | undefined,
): AcquisitionState {
  if (credits === undefined) return 'unknown';
  if (credits.balance <= 0 || credits.available < 0) return 'waiting-on-credits';
  if (window !== undefined && shedAccounts(window).length > 0) return 'partially-funded';
  return 'running';
}

/** The accounts the balance actually left out. An account the server could
 *  not price (`credits_per_hour: null`) is listed under `shed_accounts`
 *  too, but nothing was weighed against the balance for it, so it is not
 *  evidence of a shortfall. */
export function shedAccounts(window: FundedWindow): PricedAccountRef[] {
  return window.shed_accounts.filter(
    (account): account is PricedAccountRef => account.credits_per_hour !== null,
  );
}

type PricedAccountRef = FundedWindow['shed_accounts'][number] & { credits_per_hour: number };
